import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ContractTemplateLock, ContractTemplateLockResult } from 'global-intern-types';
import {
  ACQUIRE_CONTRACT_TEMPLATE_LOCK,
  GET_CONTRACT_TEMPLATE_LOCK,
  RELEASE_CONTRACT_TEMPLATE_LOCK,
} from '../api/contractTemplates';
import { fensterkennungFuer, fensterkennungMerken, neueFensterkennung } from '../services/fensterkennung';
import { sperreBeimSchliessenFreigeben } from '../services/sperreFreigeben';

// RITA-1541: Bearbeitungssperre einer Vertragsvorlage aus Sicht dieses Browserfensters.
//
// Regeln (Ticket + Entscheidungen Julia 18.10.2026): Beim Öffnen einer vorhandenen Vorlage wird die
// Sperre angefordert. Hält sie jemand anderes, kann man nur lesen – ein Übernehmen gibt es nicht.
// Eingaben (Text, Format, Platzhalter, Name, Beschreibung, Zuordnung) frischen die Sperre höchstens
// einmal pro Minute auf; nach 30 Minuten ohne Eingabe läuft sie serverseitig ab, und die nächste
// Eingabe holt sie still zurück, sofern niemand anderes inzwischen bearbeitet. Speichern beendet die
// Sperre (Server); die nächste Eingabe fordert sie wieder an. Etwa im Minutentakt sowie beim
// Zurückwechseln ins Fenster wird nachgesehen, wer gerade bearbeitet; eine frei gewordene Vorlage
// bietet „Aktuellen Stand laden und bearbeiten“. Dieselbe Person in einem zweiten Fenster darf dort
// weiterarbeiten; das alte Fenster wird dann „nur lesen“.

export type Sperrstatus =
  | 'pruefung'
  | 'bearbeitbar'
  | 'fremd'
  | 'verloren'
  | 'anderesFenster'
  | 'anderesFensterVerloren'
  | 'zweitesFensterFrage'
  | 'frei';

/** Nachsehen im Hintergrund (Minutentakt). */
export const SPERRE_NACHSEHEN_MS = 60000;
/** Eingaben frischen die eigene Sperre höchstens so oft auf. */
export const SPERRE_AUFFRISCHEN_MS = 60000;

export interface Vorlagensperre {
  status: Sperrstatus;
  /** Felder, Editor und Speichern sind gesperrt (alles außer „bearbeitbar“ und der kurzen Anfangsprüfung). */
  nurLesen: boolean;
  /** Wer die Vorlage gerade hält (für die Hinweistexte), sonst null. */
  halter: ContractTemplateLock | null;
  token: string;
  /** Eine Eingabe im Formular oder Editor (frischt die Sperre gedrosselt auf bzw. holt sie zurück). */
  eingabe: () => void;
  /**
   * Unmittelbar vor dem Speichern: false, wenn jemand anderes bearbeitet. Ohne nötige Nachfrage
   * synchron true, damit das Speichern wie bisher sofort startet.
   */
  vorSpeichern: () => boolean | Promise<boolean>;
  /** Nach erfolgreichem Speichern: der Server hat die Sperre beendet. */
  nachSpeichern: () => void;
  hierWeiterbearbeiten: () => void;
  nurLesenWaehlen: () => void;
  /** Sitzung mit dem gespeicherten Stand neu laden (frei gewordene Vorlage, „Neu laden“ nach Konflikt); die eigene Sperre bleibt. */
  aktuellenStandLaden: () => void;
}

interface Eigenschaften {
  vorlagenId: number | null;
  /** Vorlage existierte beim Öffnen (dann wird die Sperre sofort angefordert). */
  beimOeffnenAnfordern: boolean;
  /** Lädt die Sitzung mit dem aktuellen gespeicherten Stand neu (Seite tauscht die Sitzung aus). */
  onNeuLaden: () => void;
}

type Anlass = 'oeffnen' | 'eingabe' | 'wechsel';
type Antwort = { acquireContractTemplateLock: ContractTemplateLockResult };
type Nachsehen = { getContractTemplateLock: ContractTemplateLock | null };

const NUR_LESEN: ReadonlySet<Sperrstatus> = new Set<Sperrstatus>([
  'fremd',
  'verloren',
  'anderesFenster',
  'anderesFensterVerloren',
  'zweitesFensterFrage',
  'frei',
]);

/** Maßgeblich ist, ob der Server die Sperre diesem Fenster zuordnet; ohne Sperrzeile entscheidet `acquired`. */
const sperreErhalten = (ergebnis: ContractTemplateLockResult): boolean =>
  ergebnis.lock ? ergebnis.lock.this_window : ergebnis.acquired;

/**
 * Je Vorlage die zuletzt in diesem Fenster geöffnete Sitzung dieses Hooks. Eine erst nach dem Verlassen
 * bestätigte Sperre wird nur freigegeben, wenn inzwischen keine neue Sitzung derselben Vorlage (gleiche
 * Fensterkennung) geöffnet wurde – sonst würde deren Sperre gelöscht.
 */
const offeneSitzungen = new Map<number, symbol>();

export default function useVorlagensperre({
  vorlagenId,
  beimOeffnenAnfordern,
  onNeuLaden,
}: Eigenschaften): Vorlagensperre {
  const [token] = useState(() => (vorlagenId === null ? neueFensterkennung() : fensterkennungFuer(vorlagenId)));
  const [status, setStatus] = useState<Sperrstatus>(
    vorlagenId !== null && beimOeffnenAnfordern ? 'pruefung' : 'bearbeitbar',
  );
  const [halter, setHalter] = useState<ContractTemplateLock | null>(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  const idRef = useRef(vorlagenId);
  idRef.current = vorlagenId;
  /** Dieses Fenster hält nach eigener Kenntnis eine gültige Sperre. */
  const haeltRef = useRef(false);
  const letzteAuffrischungRef = useRef(0);
  const laufendRef = useRef<Promise<ContractTemplateLockResult | null> | null>(null);
  /** Gesendete Anforderungen; daran erkennt das Nachsehen eine inzwischen überholte Antwort (RITA-1600). */
  const anforderungenRef = useRef(0);
  const aktivRef = useRef(true);
  /** Beim Austausch der Sitzung im selben Fenster bleibt die Sperre bestehen (gleiche Kennung). */
  const sperreBehaltenRef = useRef(false);
  const [anfordern] = useMutation<Antwort>(ACQUIRE_CONTRACT_TEMPLATE_LOCK);
  const [freigeben] = useMutation<{ releaseContractTemplateLock: boolean }>(RELEASE_CONTRACT_TEMPLATE_LOCK);
  const freigebenRef = useRef(freigeben);
  freigebenRef.current = freigeben;

  const sitzungRef = useRef(Symbol('vorlagensperre'));

  // Eine neue Vorlage bekommt ihre Nummer mit dem ersten Speichern; ab dann gilt die Kennung für sie.
  useEffect(() => {
    if (vorlagenId === null) return undefined;
    fensterkennungMerken(vorlagenId, token);
    const sitzung = sitzungRef.current;
    offeneSitzungen.set(vorlagenId, sitzung);
    return () => {
      if (offeneSitzungen.get(vorlagenId) === sitzung) offeneSitzungen.delete(vorlagenId);
    };
  }, [vorlagenId, token]);

  const verarbeite = useCallback((ergebnis: ContractTemplateLockResult, anlass: Anlass) => {
    if (!aktivRef.current) return;
    if (sperreErhalten(ergebnis)) {
      haeltRef.current = true;
      letzteAuffrischungRef.current = Date.now();
      setHalter(ergebnis.lock);
      setStatus('bearbeitbar');
      return;
    }
    const hielt = haeltRef.current;
    haeltRef.current = false;
    if (!ergebnis.lock) {
      // Gerade hält niemand (Wettlauf); die nächste Eingabe versucht es erneut.
      setStatus('bearbeitbar');
      return;
    }
    setHalter(ergebnis.lock);
    if (ergebnis.lock.is_mine) {
      if (anlass === 'oeffnen') setStatus('zweitesFensterFrage');
      else setStatus(hielt ? 'anderesFensterVerloren' : 'anderesFenster');
    } else {
      setStatus(hielt ? 'verloren' : 'fremd');
    }
  }, []);

  const anfordernJetzt = useCallback(
    async (anlass: Anlass, switchWindow = false): Promise<ContractTemplateLockResult | null> => {
      const id = idRef.current;
      if (id === null) return null;
      if (anlass === 'eingabe' && laufendRef.current) return laufendRef.current;
      if (laufendRef.current) await laufendRef.current;
      anforderungenRef.current += 1;
      const lauf = anfordern({ variables: { id, token, switchWindow } })
        .then((antwort) => antwort.data?.acquireContractTemplateLock ?? null)
        .catch(() => null)
        .then((ergebnis) => {
          laufendRef.current = null;
          if (ergebnis) verarbeite(ergebnis, anlass);
          // Ohne Antwort (Netz) blockiert die Prüfung nicht; die Warnung beim Speichern bleibt das Netz.
          else if (aktivRef.current && statusRef.current === 'pruefung') setStatus('bearbeitbar');
          return ergebnis;
        });
      laufendRef.current = lauf;
      return lauf;
    },
    [anfordern, token, verarbeite],
  );

  const geoeffnetRef = useRef(false);
  useEffect(() => {
    if (geoeffnetRef.current) return;
    geoeffnetRef.current = true;
    if (vorlagenId !== null && beimOeffnenAnfordern) anfordernJetzt('oeffnen');
  }, [vorlagenId, beimOeffnenAnfordern, anfordernJetzt]);

  // Nachsehen: Antwort des Servers „wer bearbeitet gerade“ mit dem eigenen Stand abgleichen.
  const verarbeiteNachsehen = useCallback((lock: ContractTemplateLock | null | undefined) => {
    if (!aktivRef.current || lock === undefined) return;
    const stand = statusRef.current;
    if (stand === 'pruefung' || stand === 'zweitesFensterFrage') return;
    if (!lock) {
      if (stand === 'bearbeitbar') {
        haeltRef.current = false;
        return;
      }
      setHalter(null);
      setStatus('frei');
      return;
    }
    if (lock.this_window) {
      haeltRef.current = true;
      setHalter(lock);
      if (stand !== 'bearbeitbar') setStatus('bearbeitbar');
      return;
    }
    const hielt = haeltRef.current || stand === 'verloren' || stand === 'anderesFensterVerloren';
    haeltRef.current = false;
    setHalter(lock);
    if (lock.is_mine) setStatus(hielt ? 'anderesFensterVerloren' : 'anderesFenster');
    else setStatus(hielt ? 'verloren' : 'fremd');
  }, []);

  // Erstes Nachsehen beim Öffnen. Spätere Antworten werden unten je Abruf verarbeitet: Apollo liefert für
  // ein unverändertes Ergebnis dasselbe `data`-Objekt, sodass ein Effekt auf `data` nicht erneut läuft – der
  // Stand kann sich zwischendurch aber über das Anfordern (fremde Sperre bei einer Eingabe) geändert haben.
  // RITA-1600: Eine Nachsehen-Antwort zählt nur, wenn beim Nachsehen keine Anforderung lief und seither keine neue
  // gesendet wurde. Sonst kann sie den Stand vor dem Anfordern zeigen („keine Sperre“, obwohl der Server sie diesem
  // Fenster gerade gegeben hat) – das Fenster hielte sich dann für frei und gäbe beim Verlassen nichts frei.
  // Maßgeblich ist in diesem Fall die Antwort der Anforderung. Das erste Nachsehen läuft zusammen mit dem Anfordern
  // beim Öffnen los; es zählt deshalb nur, solange keine Anforderung gesendet wurde.
  // Ohne Deduplizierung: Apollo hängte ein Nachsehen sonst an eine noch laufende gleiche Anfrage an (etwa die erste,
  // vor dem Anfordern gelesene) – dann läse es keinen neuen Serverstand (Kollegenreview !807, P2).
  const { data, refetch } = useQuery<Nachsehen>(GET_CONTRACT_TEMPLATE_LOCK, {
    variables: { id: vorlagenId ?? 0, token },
    skip: vorlagenId === null,
    fetchPolicy: 'network-only',
    context: { queryDeduplication: false },
  });
  useEffect(() => {
    if (data && anforderungenRef.current === 0) verarbeiteNachsehen(data.getContractTemplateLock);
  }, [data, verarbeiteNachsehen]);

  // Etwa im Minutentakt und beim Zurückwechseln ins Fenster erneut nachsehen; jede nicht überholte Antwort zählt.
  useEffect(() => {
    if (vorlagenId === null) return undefined;
    const nachsehen = () => {
      const anforderungen = anforderungenRef.current;
      const liefSchon = laufendRef.current !== null;
      refetch()
        .then((ergebnis) => {
          if (liefSchon || anforderungen !== anforderungenRef.current) return;
          verarbeiteNachsehen(ergebnis?.data?.getContractTemplateLock);
        })
        .catch(() => undefined);
    };
    const sichtbar = () => {
      if (document.visibilityState === 'visible') nachsehen();
    };
    const takt = window.setInterval(nachsehen, SPERRE_NACHSEHEN_MS);
    window.addEventListener('focus', nachsehen);
    document.addEventListener('visibilitychange', sichtbar);
    return () => {
      window.clearInterval(takt);
      window.removeEventListener('focus', nachsehen);
      document.removeEventListener('visibilitychange', sichtbar);
    };
  }, [vorlagenId, refetch, verarbeiteNachsehen]);

  // Schließen des Fensters: Freigabe nach Möglichkeit; sonst läuft die Sperre nach 30 Minuten ab.
  useEffect(() => {
    const beimVerlassen = () => {
      if (haeltRef.current && idRef.current !== null) sperreBeimSchliessenFreigeben(idRef.current, token);
    };
    window.addEventListener('pagehide', beimVerlassen);
    return () => window.removeEventListener('pagehide', beimVerlassen);
  }, [token]);

  // Verlassen der Seite innerhalb der Anwendung: eigene Sperre freigeben – auch eine, die der Server erst
  // nach dem Verlassen bestätigt (Anfordern lief beim Öffnen noch oder eine Auffrischung war unterwegs:
  // sie würde die eben freigegebene Sperre sonst neu anlegen). Beim Austausch der Sitzung im selben
  // Fenster bleibt die Sperre (gleiche Kennung).
  useEffect(
    () => () => {
      aktivRef.current = false;
      const id = idRef.current;
      if (id === null || sperreBehaltenRef.current) return;
      const sperreFreigeben = () => freigebenRef.current({ variables: { id, token } }).catch(() => undefined);
      if (haeltRef.current) sperreFreigeben();
      if (laufendRef.current) {
        laufendRef.current.then((ergebnis) => {
          if (!ergebnis || !sperreErhalten(ergebnis) || sperreBehaltenRef.current) return;
          if (offeneSitzungen.has(id)) return; // Dieselbe Vorlage wurde hier inzwischen neu geöffnet.
          sperreFreigeben();
        });
      }
    },
    [token],
  );

  const eingabe = useCallback(() => {
    if (statusRef.current !== 'bearbeitbar' || idRef.current === null) return;
    if (haeltRef.current && Date.now() - letzteAuffrischungRef.current < SPERRE_AUFFRISCHEN_MS) return;
    anfordernJetzt('eingabe');
  }, [anfordernJetzt]);

  const vorSpeichern = useCallback((): boolean | Promise<boolean> => {
    const stand = statusRef.current;
    // Anfangsprüfung noch offen: nicht blockieren; der Server vergleicht den Stand ohnehin (Warnung beim Speichern).
    if (idRef.current === null || stand === 'pruefung') return true;
    if (stand !== 'bearbeitbar') return false;
    if (haeltRef.current && Date.now() - letzteAuffrischungRef.current < SPERRE_AUFFRISCHEN_MS) return true;
    return anfordernJetzt('eingabe').then((ergebnis) => (ergebnis === null ? true : ergebnis.acquired));
  }, [anfordernJetzt]);

  const nachSpeichern = useCallback(() => {
    haeltRef.current = false;
    letzteAuffrischungRef.current = 0;
  }, []);

  const hierWeiterbearbeiten = useCallback(() => {
    anfordernJetzt('wechsel', true).then((ergebnis) => {
      if (ergebnis?.acquired && aktivRef.current) {
        sperreBehaltenRef.current = true;
        onNeuLaden();
      }
    });
  }, [anfordernJetzt, onNeuLaden]);

  const nurLesenWaehlen = useCallback(() => setStatus('anderesFenster'), []);

  const aktuellenStandLaden = useCallback(() => {
    sperreBehaltenRef.current = true;
    onNeuLaden();
  }, [onNeuLaden]);

  return {
    status,
    nurLesen: NUR_LESEN.has(status),
    halter,
    token,
    eingabe,
    vorSpeichern,
    nachSpeichern,
    hierWeiterbearbeiten,
    nurLesenWaehlen,
    aktuellenStandLaden,
  };
}
